// What the ward physically holds for one patient, and what a dose takes from it.
//
// There is no ward-stock ledger and this file does not create one. on_hand is
// DERIVED by MedicationReturnableView (main/ipd/medication_order.py) as
// total_quantity − administered − returned, and this module only reads it. That
// is deliberate: prescription grain is the wrong grain for a real stock ledger
// (batches, expiry, controlled-drug custody all need item_id + batch_number), so
// keeping the number derived means it can be repointed at the pharmacy's
// item+batch ledger later without a migration.
//
// Three states, not two. "unknown" (no computable order quantity) and
// "not-dispensed" (the pharmacy has not sent it) are different facts and read
// differently to a nurse; collapsing either into "0 left" would be a lie.

const SOURCES_OUTSIDE_WARD_STOCK = ["emergency_trolley", "patient_own", "other"];

/**
 * Did this dose come out of the ward's own stock?
 * An UNSET source counts as ward stock: the picker is optional and blank by
 * default, so treating blank as "elsewhere" would decrement nothing on the
 * overwhelming majority of doses. Only an explicit other-source suppresses it.
 */
export const isWardStock = (source) => !SOURCES_OUTSIDE_WARD_STOCK.includes(source);

/**
 * Units this administration removes from the bin.
 * Zero unless the dose was actually given AND it came from ward stock — a
 * withheld or refused dose never opened the bin.
 */
export function unitsToDraw(outcome, source, units) {
  if (outcome !== "given") return 0;
  if (!isWardStock(source)) return 0;
  const n = Number(units);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Index returnable rows by prescription id. Keys are stringified so a numeric
 *  id from the order book matches a string id from the API and vice versa. */
export function indexStock(rows) {
  const out = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const id = r?.prescription_id;
    if (id === null || id === undefined || id === "") return;
    out[String(id)] = r;
  });
  return out;
}

/**
 * Stock position for one medicine.
 * @returns {{state: "known"|"unknown"|"not-dispensed", onHand: number|null}}
 */
export function stockFor(index, prescriptionId) {
  const row = index?.[String(prescriptionId)];
  // MedicationReturnableView only returns medicines that reached Dispensed or
  // Received, so an absent row means the pharmacy has not sent this one yet.
  if (!row) return { state: "not-dispensed", onHand: null };
  // The backend returns null (never 0) when it could not compute a quantity —
  // "display unknown, never a guess".
  if (row.returnable === null || row.returnable === undefined) {
    return { state: "unknown", onHand: null };
  }
  return { state: "known", onHand: Number(row.returnable) };
}
